

/*
vim: fileformat=unix syntax=JavaScript tabstop=4 shiftwidth=4 expandtab fileencoding=utf8
*/
/* jslint browser: true */
//common implementation for doclist and fw_doclens

import $ from 'jquery';
import { PdfLocationPoints } from './geometry2d';
import 'intersection-observer';

export class BundleParser {
    constructor(bundle, bundleOrder) {
        //backward compatibility
        if(!bundleOrder)
            bundleOrder = Object.keys(bundle);

        //rebuild to array of parent-child relation
        //first clone bundle, so we don't change original object
        //deep copy, because we also alter contents
        bundle = $.extend(true, {}, bundle);
        bundleOrder = bundleOrder.concat([]);

        const testDummyBundle = false;
        if(testDummyBundle) {
            for(let i = 0; i < 50; i++) {
                bundle['xxxxxxx' + i] = { title: 'Dummy' };
                bundleOrder.push('xxxxxxx' + i);
            }
        }

        const buttonBundleUid = {};
        const buttonArray = [];
        for(const id in bundle) {
            const docDef = bundle[id];
            if(docDef.bundledUid)
                buttonBundleUid[String(docDef.bundledUid)] = docDef;
        }
        //keep order, also save order
        for(let bundleIndex = 0; bundleIndex < bundleOrder.length; bundleIndex++) {
            const id = bundleOrder[bundleIndex];
            const docDef = bundle[id];
            docDef.uid = id;
            docDef.bundleIndex = bundleIndex;
            if(docDef.parentBundledUid) {
                const parentDocDef = buttonBundleUid[String(docDef.parentBundledUid)];
                if(!parentDocDef.children) {
                    parentDocDef.children = [];
                }
                parentDocDef.children.push(docDef);
            } else {
                buttonArray.push(docDef);
            }
        }

        this.bundle = bundle;
        this.buttonBundleUid = buttonBundleUid;
        this.buttonArray = buttonArray;
    }

    hideTopLevelAttachmentDuplicates() {
        const bundle = this.bundle;
        const buttonBundleUid = this.buttonBundleUid;
        const buttonArray = this.buttonArray;

        //special for Fincantieri (hide toplevel documents, if the same name is present in attachment directly below top level)
        //their document storage automatically extracts invoice attachments as top level documents
        const childAttachmentTitles = {};
        //first build direct child attachments
        for(const id in bundle) {
            const docDef = bundle[id];
            if(docDef.parentBundledUid) {
                const parentDocDef = buttonBundleUid[String(docDef.parentBundledUid)];
                if(!parentDocDef.parentBundledUid) {
                    //only top level parent
                    //multiple child attachments may have the same title (even from different parents)
                    //fix for Fincantieri - sometimes we get names in different registry and double points (..PDF)
                    const title = (docDef.title || '').toLowerCase().replace(/\.+/g, '.');
                    let titleDef = childAttachmentTitles[title];
                    if(!titleDef) {
                        titleDef = { parents: {} };
                        childAttachmentTitles[title] = titleDef;
                    }
                    titleDef.parents[String(docDef.parentBundledUid)] = true;
                }
            }
        }

        for(let i = 0; i < buttonArray.length; i++) {
            const docDef = buttonArray[i];
            const title = (docDef.title || '').toLowerCase().replace(/\.+/g, '.');
            const sameChild = childAttachmentTitles[title];
            if(!sameChild)
                continue;
            //we have duplicate, lets check:
            //if the same title is child of multiple parents - we skip it. there are so many edge cases to check, so better just to skip
            if(Object.keys(sameChild.parents).length > 1)
                continue;
            //if the same title is direct child of parent we are going to remove - skip it
            if(sameChild.parents[String(docDef.bundledUid)])
                continue;
            //remove this button
            buttonArray.splice(i, 1);
            i--;
        }
    }

    // eslint-disable-next-line class-methods-use-this
    _tryGetDocumentAttachment(mainDocument, initialShowAttachments) {
        function isPatternMatch(doc, patternObj) {
            let regex = null;
            let testString = null;
            if(patternObj.nameRegex) {
                regex = patternObj.nameRegex;
                testString = doc.title;
            } else if(patternObj.typeRegex) {
                regex = patternObj.typeRegex;
                testString = doc.contentType && doc.contentType.type;
            } else if(patternObj.subTypeRegex) {
                regex = patternObj.subTypeRegex;
                testString = doc.contentType && doc.contentType.subType;
            }
            if(!regex || !testString)
                return false;
            regex = new RegExp(regex, 'i');
            return regex.test(testString);
        }
        function recursiveFindAttachment(parentDocument, initialShowAttachment) {
            if(!parentDocument.children || !parentDocument.children.length)
                return null;
            for(const child of parentDocument.children) {
                if(isPatternMatch(child, initialShowAttachment.attachment))
                    return child;
                const recursiveChild = recursiveFindAttachment(child, initialShowAttachment);
                if(recursiveChild)
                    return recursiveChild;
            }
            return null;
        }

        if(!mainDocument.children || !mainDocument.children.length)
            return null;
        for(const initialShowAttachment of initialShowAttachments) {
            if(isPatternMatch(mainDocument, initialShowAttachment.document)) {
                const showAttachment = recursiveFindAttachment(mainDocument, initialShowAttachment);
                if(showAttachment)
                    return showAttachment;
            }
        }
        return null;
    }
    getSelectedId(mainDocumentId, initialShowAttachments) {
        const bundle = this.bundle;
        const buttonArray = this.buttonArray;
        if(!buttonArray || !buttonArray.length)
            return null;

        let mainDocument = bundle[mainDocumentId];
        if(!mainDocument)
            mainDocument = buttonArray[0];

        if(initialShowAttachments && Array.isArray(initialShowAttachments) && initialShowAttachments.length) {
            const attachmentDocument = this._tryGetDocumentAttachment(mainDocument, initialShowAttachments);
            if(attachmentDocument)
                mainDocument = attachmentDocument;
        }
        return mainDocument.uid;
    }
}

//we request renderer pages in max dimmensions to fit screen width and height
export function PagesInfo(ww, wh, pagesData, _docId, _representation) {
    const pages = [];
    const pagesdata = [];
    const pagesCount = pagesData.count;
    for(let i = 0; i < pagesCount; i++) {
        const pageInfo = pagesData.page[i];
        const pageWidthPt = pageInfo.x;
        const pageHeightPt = pageInfo.y;
        const dx = pageWidthPt / ww;
        const dy = pageHeightPt / wh;
        const ratio = Math.min(dx, dy);
        const pageDim = new PdfLocationPoints({ w: pageWidthPt, h: pageHeightPt, pointsToScrenRatio: ratio });
        pages.push(pageDim);
        pagesdata.push({ urls: {} });
    }

    this.getPageInfo = function(index) {
        let page = pages[index];
        if(!page)
            page = new PdfLocationPoints({ w: 1, h: 1, pointsToScrenRatio: 1 });
        return page;
    };

    this.getPageUrlParams = function(index, rotation, device) {
        //use global, if not in args
        let docId = _docId;
        if(docId === undefined) {
            docId = PagesInfo.docId;
            if(typeof (docId) === 'function')
                docId = docId();
        }

        const page = this.getPageInfo(index).toPixels();
        const params = {
            document: docId,
            width: page.w_r,
            height: page.h_r,
            page: (index + 1),
            rotation,
            //use global, if not in args
            representation: (_representation === undefined) ? $('#vwr-representation').val() : _representation,
        };
        if(device)
            params.device = device;
        return params;
    };

    this.getPageUrlAsync = function(index, rotation, device) {
        const pagedata = pagesdata[index];
        if(!pagedata)
            return Promise.resolve({ renderer: '', status: 'ok', url: '' });
        const pagedef = JSON.stringify({ rotation, device });
        let cachedUrl = pagedata.urls[pagedef];
        if(cachedUrl) {
            return Promise.resolve(cachedUrl);
        }

        const params = this.getPageUrlParams(index, rotation, device);
        //we don't have access to errutil.scripterr here, so we always show error, but allow to catch externally
        return PagesInfo.getrender(params, { noalert: false, errors: true })
            .then((data) => {
                cachedUrl = data;
                pagedata.urls[pagedef] = cachedUrl;
                return cachedUrl;
            });
    };
    //for backward compatibility
    this.getPageUrl = function(index, rotation, device, cb) {
        this.getPageUrlAsync(index, rotation, device)
            .then((data) => cb(data.url, data))
            .catch((err) => { /**/ });
    };

    this.pageChanged = function(index) {
        const pagedata = pagesdata[index];
        if(pagedata)
            pagedata.urls = {};
    };

    this.documentChanged = function() {
        for(let i = 0; i < pagesdata.length; i++) {
            this.pageChanged(i);
        }
    };
}
//must be initialized externally, may be function
PagesInfo.docId = null;
//tools.api.bind(tools, 'viewerui/getrender.cmd')
PagesInfo.getrender = function() { return Promise.resolve(); };


export const DocumentUtil = {};
//this will load all thumbnails at once by default
//too stressfull for server to render many documents at once
//for sequential processing see LazySequentialThumbnailLoader (for ui)
//for on-screen scrolling sequential processing see LazyOnScreenThumbnailLoader (for doclist)
DocumentUtil.lazyLoadThumbnails = function(bundle, selector, loadedCallback) {
    if(!selector)
        selector = $('#attachmentThumbnails .document-thumbnail img.thumbnail-loader');
    if(!loadedCallback)
        loadedCallback = (err) => { /**/ };
    selector.off('load').on('load', function() {
        //we assume every document has at least one page
        const img = $(this);
        //no reentry
        img.off('load');
        const thumbnail = img.closest('.document-thumbnail');
        const uid = thumbnail.attr('data-docid');
        const ww = 94;
        const wh = 118;

        const _pagesData = {
            count: 1,
            page: [{
                x: ww,
                y: wh,
            }],
        };
        //force default representation
        const _pagesInfo = new PagesInfo(ww, wh, _pagesData, uid, '');
        _pagesInfo.getPageUrlAsync(0, 0, null)
            .then((data) => {
                const { renderer: rendererType, url } = data;
                //we may get either image (for pdf, image) or html
                switch(rendererType) {
                    case 'html':
                    {
                    //make img transparent, place iframe behind img
                        const iframe = $('<iframe class="thumbnail-loader" scrolling="no" sandbox="allow-same-origin"></iframe>').insertAfter(img);
                        iframe.off('load').on('load', function() {
                            img.attr('src', 'images/loading-thumbnail-transparent.png').attr('width', ww).attr('height', wh);

                            // iframe is from same domain, so we can modify dom tree for it
                            const scale = '0.3';
                            let style = 'html, body { margin: 0 !important; height: 100% !important; overflow: hidden !important; width: 100vw !important; }';

                            style += `body > * {
-ms-transform: scale(${scale});
-moz-transform: scale(${scale});
-o-transform: scale(${scale});
-webkit-transform: scale(${scale});
transform: scale(${scale});
-ms-transform-origin: 0 0;
-moz-transform-origin: 0 0;
-o-transform-origin: 0 0;
-webkit-transform-origin: 0 0;
transform-origin: 0 0;
}`;

                            iframe.contents().find('head').append($(`<style type="text/css">${style}</style>
<meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=no">`));
                            loadedCallback();
                        });

                        iframe.attr('src', url);
                        break;
                    }
                    case 'img':
                    case 'pdf':
                        img.off('load').on('load', function() {
                            img.attr('width', ww).attr('height', wh);
                            loadedCallback();
                        });
                        img.attr('src', url);
                        break;
                    default:
                        loadedCallback();
                }
            })
            .catch((err) => {
                img.attr('src', 'images/loading-thumbnail-error.png').attr('width', ww).attr('height', wh).addClass('thumbnail-error');
                loadedCallback(err);
            });
    });
};

//tools.api.bind(tools, 'viewerui/documentbyotp.cmd')
DocumentUtil.determineDocumentId = function(purl, api) {
    return Promise.resolve()
        .then(() => {
        // parse the current page URL
            const url = purl();
            let docId = url.param('document');
            if(docId)
                return docId;
            const docOtp = url.param('doc_otp');
            if(!docOtp)
                throw new Error('Document not selected!');
            //check if we stored in session storage
            const storage = window.sessionStorage;
            const storageKey = 'dscloud-otp-' + docOtp;
            if(storage && storage.getItem) {
                docId = storage.getItem(storageKey);
                if(docId)
                    return docId;
            }

            return api({ doc_otp: docOtp }, { errors: true, waiting: true, noalert: true })
                .then((data) => {
                    docId = data.document;
                    if(storage && storage.setItem) {
                        storage.setItem(storageKey, docId);
                    }
                    return docId;
                });
        });
};


class LazySequentialThumbnailLoaderQueue {
    constructor(THUMBNAIL_SAFE_INTERVAL) {
        this.loaders = new Map();
        this.thumbnailQueue = new Map();
        this.processingRenderQueue = false;
        //if user scrolls too fast, we will skip thumbnails which are already scrolled out
        this.THUMBNAIL_SAFE_INTERVAL = THUMBNAIL_SAFE_INTERVAL || 400;
    }
    addThumbnailLoader(thumbnailLoader) {
        this.loaders.set(thumbnailLoader.uid, thumbnailLoader);
    }
    addQueue(uid) {
        if(this.thumbnailQueue.has(uid))
            return;
        const thumbnailLoader = this.loaders.get(uid);
        if(thumbnailLoader.thumbnailLoaded)
            return;
        this.thumbnailQueue.set(uid, {
            timeInserted: new Date(),
        });
        this.wakeupQueue();
    }
    getQueueHead() {
        //iteration in insertion order
        const iterator = this.thumbnailQueue.entries();
        const [uid, queueHead] = iterator.next().value;
        const now = new Date();
        const timeout = now.getTime() - queueHead.timeInserted.getTime();
        return {
            uid,
            queueHead,
            timeout,
        };
    }
    restartQueue() {
        this.processingRenderQueue = false;
        this.wakeupQueue();
    }
    wakeupQueue() {
        if(!this.thumbnailQueue.size) {
            this.processingRenderQueue = false;
            return;
        }
        if(this.processingRenderQueue)
            return;
        this.processingRenderQueue = true;
        const { uid, timeout } = this.getQueueHead();
        if(timeout < this.THUMBNAIL_SAFE_INTERVAL) {
            setTimeout(() => {
                this.restartQueue();
            }, this.THUMBNAIL_SAFE_INTERVAL - timeout);
            return;
        }

        //do this outside, so caller will finish asap
        setTimeout(() => {
            const thumbnailLoader = this.loaders.get(uid);
            this.thumbnailQueue.delete(uid);

            let loadThumbnailTimer = setTimeout(() => {
                //if thumbnail never loaded
                loadThumbnailTimer = null;
                this.restartQueue();
            }, 10000);
            thumbnailLoader.loadThumbnail((err) => {
                if(loadThumbnailTimer) {
                    clearTimeout(loadThumbnailTimer);
                    loadThumbnailTimer = null;
                    this.restartQueue();
                }
            });
        }, 1);
    }
    static uidFromImage(img) {
        const thumbnail = img.closest('.document-thumbnail');
        const uid = thumbnail.attr('data-docid');
        return uid;
    }
}

class LazyOnScreenThumbnailObserver extends LazySequentialThumbnailLoaderQueue {
    constructor() {
        super();
        const io = new IntersectionObserver((entries) => {
            for(const entry of entries) {
                const img = $(entry.target);
                const uid = LazySequentialThumbnailLoaderQueue.uidFromImage(img);
                if(entry.isIntersecting) {
                    this.addQueue(uid);
                } else {
                    this.thumbnailQueue.delete(uid);
                }
            }
        }, { threshold: 0.1 });
        io.POLL_INTERVAL = 1000;
        this.io = io;
    }
    addThumbnailLoader(thumbnailLoader) {
        super.addThumbnailLoader(thumbnailLoader);
        this.io.observe(thumbnailLoader.img);
    }
}

class LazyThumbnailLoaderBase {
    constructor(bundle, img) {
        this.bundle = bundle;
        this.img = img[0];
        this.uid = LazySequentialThumbnailLoaderQueue.uidFromImage(img);
        this.placeholderLoaded = false;
        this.thumbnailLoaded = false;
        this.thumbnailFailed = false;
        img.off('load').on('load', () => {
            img.off('load');
            this.placeholderLoaded = true;
        });
    }
    loadThumbnail(loadedCallback) {
        if(this.thumbnailLoaded) {
            loadedCallback();
            return;
        }

        this.thumbnailLoaded = true;
        const img = $(this.img);
        DocumentUtil.lazyLoadThumbnails(this.bundle, img, (err) => {
            if(err)
                this.thumbnailFailed = true;
            loadedCallback(err);
        });

        if(this.placeholderLoaded) {
            img.triggerHandler('load');
        }
        this.placeholderLoaded = true;
    }
    static getThumbnailByUid(loader, uid) {
        if(!loader)
            return null;
        return loader.loaders.get(uid);
    }
}

export class LazySequentialThumbnailLoader extends LazyThumbnailLoaderBase {
    enqueueLoader() {
        let queue = LazySequentialThumbnailLoader.QUEUE;
        if(!queue) {
            queue = new LazySequentialThumbnailLoaderQueue();
            LazySequentialThumbnailLoader.QUEUE = queue;
        }
        queue.addThumbnailLoader(this);
        queue.addQueue(this.uid);
    }
    static enqueueAllLoaders(bundle) {
        const selector = $('#attachmentThumbnails .document-thumbnail img.thumbnail-loader');
        selector.each(function() {
            new LazySequentialThumbnailLoader(bundle, $(this)).enqueueLoader();
        });
    }
    static getThumbnailByUid(uid) {
        return super.getThumbnailByUid(LazySequentialThumbnailLoader.QUEUE, uid);
    }
}
LazySequentialThumbnailLoader.QUEUE = null;

export class LazyOnScreenThumbnailLoader extends LazyThumbnailLoaderBase {
    startObserving() {
        if(!LazyOnScreenThumbnailLoader.OBSERVER)
            LazyOnScreenThumbnailLoader.OBSERVER = new LazyOnScreenThumbnailObserver();
        LazyOnScreenThumbnailLoader.OBSERVER.addThumbnailLoader(this);
    }
    static getThumbnailByUid(uid) {
        return super.getThumbnailByUid(LazyOnScreenThumbnailLoader.OBSERVER, uid);
    }
}
LazyOnScreenThumbnailLoader.OBSERVER = null;
